export interface MembershipInfo {
  tier: number;
  monthly_minutes: number;
  used_minutes: number;
  remaining_minutes: number;
  billing_period: {
    type: 'stripe_billing' | 'calendar_month';
    start_date: string;
    end_date: string;
    days_remaining: number;
    subscription_status:
      | 'active'
      | 'trialing'
      | 'past_due'
      | 'canceled'
      | 'inactive';
  };
}

export interface MembershipInfoError {
  error: string;
}

export type MembershipInfoApiResponse = MembershipInfo | MembershipInfoError;

export interface UsageRecord {
  id: number;
  duration_seconds: number;
  feature_used: string;
  created_at: string;
}

export interface UsageHistory {
  usage_history: UsageRecord[];
  total_records: number;
}

export interface RecordUsageRequest {
  duration_seconds: number;
  feature_used?: string;
}

export interface RecordUsageResponse {
  success: boolean;
  remaining_minutes: number;
  usage_recorded: number;
  error?: string;
}

export interface SubscriptionTier {
  id: number;
  name: string;
  tier_level: number;
  monthly_minutes: number;
  stripe_price_id: string;
}

export interface AvailableTiers {
  tiers: SubscriptionTier[];
}

export interface SubscribeRequest {
  tier_level: number;
  success_url?: string;
  cancel_url?: string;
}

export interface SubscribeResponse {
  checkout_url: string;
  session_id: string;
}

export interface SubscriptionStatus {
  tier: string;
  tier_id: number;
  subscription_status: string;
  stripe_customer_id: string;
  stripe_subscription_id: string;
  current_period_start: number;
  current_period_end: number;
  cancel_at_period_end: boolean;
}

export interface SubscriptionActionResponse {
  message: string;
  cancel_at_period_end?: boolean;
  current_period_end?: string;
}

export interface ManageSubscriptionResponse {
  portal_url: string;
}

export interface PreviewUpdateRequest {
  tier_level: number;
}

export interface PreviewUpdateResponse {
  current_tier: string;
  new_tier: string;
  current_tier_minutes: number;
  new_tier_minutes: number;
  current_period_end: number | null;
  message: string;
  currency: string;
}

export interface UpdateSubscriptionRequest {
  tier_level: number;
}

export interface UpdateSubscriptionResponse {
  message: string;
  new_tier: string;
  subscription_status: string;
  current_period_end: string;
  payment_status?: string;
  requires_action?: boolean;
  client_secret?: string;
  payment_intent_id?: string;
}

export interface UpdateCheckoutRequest {
  tier_id: number;
}

export interface UpdateCheckoutResponse {
  checkout_url: string;
  session_id: string;
  current_tier: string;
  new_tier: string;
}

export interface UpgradeNextCycleRequest {
  tier_level: number;
}

export interface UpgradeNextCycleResponse {
  checkout_url: string;
  session_id: string;
  current_tier: string;
  new_tier: string;
  current_period_end: string;
  message: string;
}

export interface UpdateSessionUsageRequest {
  minutes_used: number;
  session_type: string;
}

export interface UpdateSessionUsageResponse {
  success: boolean;
  minutes_used: number;
  session_type: string;
  remaining_minutes: number;
  total_monthly_minutes: number;
  used_this_month: number;
  overage_minutes: number;
  is_over_limit: boolean;
  stripe_usage_recorded: boolean;
  usage_record_id?: number;
}

export interface SessionUsageError {
  error: string;
  remaining_minutes: number;
  requested_minutes: number;
  is_free_user: boolean;
  upgrade_required: boolean;
}

export interface DowngradeNextCycleRequest {
  tier_level: number;
}

export interface DowngradeNextCycleResponse {
  success: boolean;
  action:
    | 'subscription_cancellation_scheduled'
    | 'subscription_downgrade_scheduled';
  current_tier: string;
  target_tier: string;
  effective_date: number;
  cancel_at_period_end?: boolean;
  schedule_id?: string;
  message: string;
}

class MembershipService {
  private baseURL = process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : 'https://stronghireai.com';

  getTierName(tier: number): string {
    const tierNames = {
      1: 'Starter (Free)',
      2: 'Practice',
      3: 'Pro',
      4: 'Onsite',
      5: 'Finalist',
      6: 'Strong Hire',
    };
    return tierNames[tier as keyof typeof tierNames] || 'Unknown';
  }

  async getMembershipInfo(): Promise<MembershipInfo> {
    const response = await fetch(`${this.baseURL}/membership/info/`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch membership info');
    }

    const data: MembershipInfoApiResponse = await response.json();


    // Check if the response is an error
    if ('error' in data) {
      throw new Error(data.error);
    }

    return data;
  }

  async getUsageHistory(): Promise<UsageHistory> {
    const response = await fetch(`${this.baseURL}/membership/history/`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch usage history');
    }

    return response.json();
  }

  async recordUsage(
    usageData: RecordUsageRequest
  ): Promise<RecordUsageResponse> {
    const response = await fetch(`${this.baseURL}/membership/record-usage/`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(usageData),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to record usage');
    }

    return data;
  }

  // Subscription Management Methods
  async getAvailableTiers(): Promise<AvailableTiers> {
    const response = await fetch(`${this.baseURL}/membership/tiers/`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch available tiers');
    }

    return response.json();
  }

  async subscribe(subscribeData: SubscribeRequest): Promise<SubscribeResponse> {
    const response = await fetch(`${this.baseURL}/membership/subscribe/`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(subscribeData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create subscription');
    }

    return response.json();
  }

  async getSubscriptionStatus(): Promise<SubscriptionStatus> {
    const response = await fetch(
      `${this.baseURL}/membership/subscription/status/`,
      {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch subscription status');
    }

    return response.json();
  }

  async cancelSubscription(): Promise<SubscriptionActionResponse> {
    const response = await fetch(
      `${this.baseURL}/membership/subscription/cancel/`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to cancel subscription');
    }

    return response.json();
  }

  async reactivateSubscription(): Promise<SubscriptionActionResponse> {
    const response = await fetch(
      `${this.baseURL}/membership/subscription/reactivate/`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to reactivate subscription');
    }

    return response.json();
  }

  async manageSubscription(): Promise<ManageSubscriptionResponse> {
    const response = await fetch(
      `${this.baseURL}/membership/subscription/manage/`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.error || 'Failed to create customer portal session'
      );
    }

    return response.json();
  }

  async previewSubscriptionUpdate(
    tierLevel: number
  ): Promise<PreviewUpdateResponse> {
    const response = await fetch(
      `${this.baseURL}/membership/subscription/preview-update/`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tier_level: tierLevel }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to preview subscription update');
    }

    return response.json();
  }

  async updateSubscription(
    tierLevel: number
  ): Promise<UpdateSubscriptionResponse> {
    const response = await fetch(
      `${this.baseURL}/membership/subscription/update/`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tier_level: tierLevel }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update subscription');
    }

    return response.json();
  }

  async updateSubscriptionCheckout(
    tierLevel: number
  ): Promise<UpdateCheckoutResponse> {
    const response = await fetch(
      `${this.baseURL}/membership/subscription/update-checkout/`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tier_level: tierLevel }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.error || 'Failed to create update checkout session'
      );
    }

    return response.json();
  }

  async upgradeNextCycle(tierLevel: number): Promise<UpgradeNextCycleResponse> {
    const response = await fetch(
      `${this.baseURL}/membership/subscription/upgrade-next-cycle/`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tier_level: tierLevel }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.error || 'Failed to schedule upgrade for next cycle'
      );
    }

    return response.json();
  }

  async updateSessionUsage(
    usageData: UpdateSessionUsageRequest
  ): Promise<UpdateSessionUsageResponse> {
    const response = await fetch(
      `${this.baseURL}/membership/update-session-usage/`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(usageData),
      }
    );

    if (!response.ok) {
      if (response.status === 403) {
        // Free user limit exceeded
        const error: SessionUsageError = await response.json();
        const usageError = new Error(
          error.error || 'Usage limit exceeded'
        ) as any;
        usageError.isUsageError = true;
        usageError.details = error;
        throw usageError;
      }
      const error = await response.json();
      throw new Error(error.error || 'Failed to update session usage');
    }

    return response.json();
  }

  async downgradeNextCycle(
    tierLevel: number
  ): Promise<DowngradeNextCycleResponse> {
    const response = await fetch(
      `${this.baseURL}/membership/subscription/downgrade-next-cycle/`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tier_level: tierLevel }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.error || 'Failed to schedule downgrade for next cycle'
      );
    }

    return response.json();
  }
}

const membershipService = new MembershipService();
export default membershipService;
